import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, tap, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { NgxPaginationModule } from 'ngx-pagination';
import * as XLSX from 'xlsx';

import { AppState, IUniverseSelection } from '../../../../../../state/app.state';
import { loadSidenavData, submitSearch, changeActiveSideTab, setUniverseSelection } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey, selectUniverseSelectionbyTabId } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { ITopCompanies } from '../../../../../../core/models/results.model';
import { getBarColor } from '../../../../../../shared/utils/bracket.utils';
import { NullableNumberPipe } from '../../../../../../shared/pipes/nullable-number.pipe';
import { REGION_CODES } from 'public/country-region-codes';
import { UniverseSelectorComponent } from 'src/app/shared/components/universe-selector/universe-selector.component';
import { CompanyThemePopupComponent } from '../../general/company-theme-popup/company-theme-popup.component';
import { AddToWatchlistPopupComponent } from 'src/app/shared/components/add-to-watchlist-popup/add-to-watchlist-popup.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';

export type ThematicCompetitorMode = 'public' | 'private' | 'custom';

const SIDENAV_KEY = 'thematic_top_companies';

const ALL_CATEGORY_TABS = [
  {
    key: 'all_competitors',
    label: 'All Competitors',
    tooltip: 'All companies in theme, no filter, innovation score ranking.',
    description: 'All companies in the selected peer group, ranked by innovation score.'
  },
  {
    key: 'innovation_leaders',
    label: 'Innovation Leaders',
    tooltip: 'Innovation Leaders are companies that rank highest among competitors based on their innovation score, a composite score reflecting size, growth and quality, relative to the peer group.',
    description: 'Innovation Leaders are the 50 companies that stand out with a strong innovation activity balanced across portfolio size, recent growth and innovation quality.'
  },
  {
    key: 'growth_leaders',
    label: 'Growth Leaders',
    tooltip: 'Growth Leaders are companies with the highest relative growth, among companies with at least 10 inventions published in the last 12 months.',
    description: 'Growth Leaders are the 50 companies which show the most significant recent acceleration in their innovation activity.'
  },
  {
    key: 'size_leaders',
    label: 'Size Leaders',
    tooltip: 'Size Leaders are companies with the highest number of inventions published in the last 10 years.',
    description: 'Size Leaders are the 50 companies with the largest innovation portfolios among competitors, responsible for the largest share of the innovation in their space.'
  },
  {
    key: 'quality_leaders',
    label: 'Quality Leaders',
    tooltip: 'Quality Leaders are companies with the highest innovation quality, among companies with at least 10 inventions in the last 12m.',
    description: 'Quality Leaders are the 50 companies that prioritize quality over quantity, likely to produce the largest individual breakthroughs.'
  },
  {
    key: 'upcoming',
    label: 'Up and Coming',
    tooltip: 'Up and Coming are companies with the highest innovation score among companies with at least a third of their inventions filed in the last year.',
    description: 'Up and Coming are the 50 companies that have the strongest innovation activity among companies that have recently increased their efforts in this technology area.'
  },
];

@Component({
  selector: 'app-thematic-competitors-table-v2',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule,
    MatTooltipModule, MatSlideToggleModule,
    NgxPaginationModule, NullableNumberPipe,
  ],
  templateUrl: './thematic-competitors-table-v2.component.html',
  styleUrl: './thematic-competitors-table-v2.component.css',
})
export class ThematicCompetitorsTableV2Component implements OnInit, OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() query: string | null = null;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() mode: ThematicCompetitorMode = 'public';
  @Input() theme_params: unknown = null;
  
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // All companies from API (unfiltered)
  allCompanies: ITopCompanies[] = [];
  // Companies filtered by mode
  modeFilteredCompanies: ITopCompanies[] = [];
  filteredCompanyList: ITopCompanies[] = [];

  showSpinner = false;
  showDataMissing = false;
  loadingFailed = false;
  paginator_1_page = 1;
  showOnlyFavorites = false;
  searchQuery = '';
  isSearching = false;
  showQueryBox = false;
  countriesControl = new FormControl<string[]>([]);
  expandedRegions = new Set<string>();
  countryFilterOptions = REGION_CODES;
  selectedCompanies = new Set<string>();

  // Universe selector (custom mode only)
  universeSelection: IUniverseSelection = { include_public: true, include_private: true };
  hasCustomSelection = false;

  sortColumn = 'innovation_score';
  sortDirection: 'asc' | 'desc' = 'desc';
  selectedCategory = 'all_competitors';

  get categoryTabs() {
    // Private mode has no "Up and Coming" category
    if (this.mode === 'private') {
      return ALL_CATEGORY_TABS.filter(c => c.key !== 'upcoming');
    }
    return ALL_CATEGORY_TABS;
  }

  get paginationId(): string {
    return `pag_thematic_${this.mode}`;
  }

  get modeDescription(): string {
    switch (this.mode) {
      case 'public':
        return 'All public companies active with at least 1 invention in this area in the last 24 months, ranked based on the level of their innovative output in this area.';
      case 'private':
        return 'Private companies within the theme that are active with at least 1 invention in this area in the last 24 months, ranked based on the level of their innovative output in this area.';
      case 'custom':
        return 'The table below shows companies within the selected peer group that are active with at least 1 invention in this area in the last 24 months, ranked based on the level of their innovative output in this area.';
    }
  }

  getBarColor = getBarColor;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(query => {
      this.searchQuery = query;
      this.isSearching = false;
      this.applyFilters();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.resetState();
      if (this.mode === 'custom') {
        this.loadDataCustomMode();
      } else {
        this.loadData();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Data Loading ---

  // Custom mode → backend filters by the user's universeSelection; per-filter cache key.
  // Public/Private → shared base set, frontend filters by companytype only.
  private cacheKey(): string {
    if (this.mode !== 'custom') return SIDENAV_KEY;
    const u = this.universeSelection;
    return `${SIDENAV_KEY}_custom:${u.include_public ? 1 : 0}_${u.include_private ? 1 : 0}_${u.min_mcap ?? ''}_${u.max_mcap ?? ''}`;
  }

  private themeParamsForBackend(): { cluster_id: string | null; cluster_name: string | null; theme_search_type: string } {
    const p = (this.theme_params as any)?.params ?? {};
    return {
      cluster_id: p.cluster_id ?? null,
      cluster_name: p.cluster_name ?? this.tab_name ?? null,
      theme_search_type: p.theme_search_type ?? 'cluster',
    };
  }

  private filtersForMode(): { include_public: boolean; include_private: boolean; min_mcap?: number; max_mcap?: number } {
    if (this.mode !== 'custom') return { include_public: true, include_private: true };
    return {
      include_public: !!this.universeSelection.include_public,
      include_private: !!this.universeSelection.include_private,
      min_mcap: this.universeSelection.min_mcap,
      max_mcap: this.universeSelection.max_mcap,
    };
  }

  private loadData(): void {
    // Clear stale rows so the spinner shows while the new selection loads.
    this.allCompanies = [];
    this.modeFilteredCompanies = [];
    this.filteredCompanyList = [];
    this.showSpinner = true;
    this.showDataMissing = false;
    this.loadingFailed = false;
    const currentTabId = this.tabId;
    const sidenavKey = this.cacheKey();

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, sidenavKey)).pipe(
      take(1),
      switchMap(cached => {
        if (cached) {
          if (this.tabId === currentTabId) {
            this.showSpinner = false;
            this.applySnapshot(cached);
          }
          return of(null);
        }
        return this.apiService.getTopCompanies({
          ...this.themeParamsForBackend(),
          query: this.query,
          tabId: currentTabId,
          ...this.filtersForMode(),
        }).pipe(
          takeUntil(this.destroy$),
          tap(res => {
            if (res.status === 204 || !res.body?.payload?.results?.length) {
              if (this.tabId === currentTabId) {
                this.showSpinner = false;
                this.showDataMissing = true;
              }
              return;
            }
            const payload = res.body.payload;
            const targetTabId = payload.tabId !== currentTabId ? payload.tabId : currentTabId;
            this.store.dispatch(loadSidenavData({
              tabId: targetTabId,
              sidenavKey,
              data: payload.results,
            }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.showSpinner = false;
              this.applySnapshot(payload.results);
            }
          }),
        );
      }),
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.loadingFailed = true;
          this.showSpinner = false;
        }
      },
    });
  }

  private isRealCustomSelection(u: IUniverseSelection | undefined): boolean {
    if (!u) return false;
    const onlyPublic = u.include_public && !u.include_private && !u.min_mcap && !u.max_mcap;
    const onlyPrivate = u.include_private && !u.include_public && !u.min_mcap && !u.max_mcap;
    return !onlyPublic && !onlyPrivate;
  }

  private loadDataCustomMode(): void {
    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(data => {
      if (this.isRealCustomSelection(data?.universe)) {
        this.universeSelection = data!.universe!;
        this.hasCustomSelection = true;
        this.loadData();
      } else {
        // No valid selection yet — open the selector immediately. The dialog has no
        // need for prior data; user will pick filters, then loadData() runs.
        setTimeout(() => this.openUniverseSelector(), 100);
      }
    });
  }

  private applySnapshot(data: ITopCompanies[]): void {
    this.allCompanies = data.map(c => ({ ...c, favorite: c.favorite ?? false }));
    this.buildFilteredList();
  }

  private buildFilteredList(): void {
    let companies = this.allCompanies;

    if (this.mode === 'public') {
      companies = companies.filter(c =>
        c.companytype === 'Public Company' || c.companytype === 'Public Investment Firm'
      );
    } else if (this.mode === 'private') {
      companies = companies.filter(c =>
        c.companytype !== 'Public Company' && c.companytype !== 'Public Investment Firm'
      );
    }
    // custom mode → backend already filtered; use payload as-is.

    this.modeFilteredCompanies = companies;
    this.applyFilters();
  }

  // --- Filtering & Sorting ---

  applyFilters(): void {
    this.filteredCompanyList = this.applyCategoryFilter([...this.modeFilteredCompanies]);
    this.applyCategoryRanking(this.filteredCompanyList);

    if (this.countriesControl.value?.length) {
      this.filteredCompanyList = this.filteredCompanyList.filter(c =>
        this.countriesControl.value!.includes(c.country)
      );
    }

    if (this.showOnlyFavorites) {
      this.filteredCompanyList = this.filteredCompanyList.filter(c =>
        this.selectedCompanies.has(c.entityid) || c.favorite
      );
      this.paginator_1_page = 1;
      return;
    }

    if (this.searchQuery) {
      this.filteredCompanyList = this.filteredCompanyList.filter(c =>
        c.entity_name?.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }

    if (this.selectedCategory === 'all_competitors' && this.sortColumn) {
      this.applySorting();
    }
  }

  private applyCategoryFilter(companies: ITopCompanies[]): ITopCompanies[] {
    if (!companies.length) return [];
    switch (this.selectedCategory) {
      case 'innovation_leaders':
        return companies
          .filter(c => c.innovation_score > 0)
          .sort((a, b) => b.innovation_score - a.innovation_score)
          .slice(0, 50);
      case 'growth_leaders':
        return companies
          .filter(c => c.invention_count_12m >= 10 && c.relative_growth_12m > 0)
          .sort((a, b) => b.relative_growth_12m - a.relative_growth_12m)
          .slice(0, 50);
      case 'size_leaders':
        return companies
          .filter(c => c.in_theme_invention_count_120m > 0)
          .sort((a, b) => (b.in_theme_invention_count_120m ?? 0) - (a.in_theme_invention_count_120m ?? 0))
          .slice(0, 50);
      case 'quality_leaders':
        return companies
          .filter(c => c.invention_count_12m >= 10 && c.quality_average_12m > 69)
          .sort((a, b) => b.quality_average_12m - a.quality_average_12m)
          .slice(0, 50);
      case 'upcoming':
        return companies
          .filter(c => {
            const inv1y = c.invention_count_12m;
            const inv10y = c.in_theme_invention_count_120m;
            return inv1y >= 10 && c.relative_growth_12m > 0 && (inv10y > 0 ? (inv1y / inv10y) >= 0.33 : false);
          })
          .sort((a, b) => b.innovation_score - a.innovation_score)
          .slice(0, 50);
      case 'all_competitors':
      default:
        return companies.sort((a, b) => b.innovation_score - a.innovation_score);
    }
  }

  private applyCategoryRanking(companies: ITopCompanies[]): void {
    if (this.selectedCategory === 'all_competitors') {
      companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
    }
    companies.forEach((c, i) => c.rank = i + 1);
  }

  selectCategory(key: string): void {
    this.selectedCategory = key;
    this.paginator_1_page = 1;
    if (key !== 'all_competitors') {
      this.sortColumn = 'innovation_score';
      this.sortDirection = 'desc';
    }
    this.applyFilters();
  }

  sort(column: string): void {
    if (this.selectedCategory !== 'all_competitors') return;
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.applySorting();
  }

  private applySorting(): void {
    if (!this.sortColumn) return;
    this.paginator_1_page = 1;
    this.filteredCompanyList.sort((a, b) => {
      let aVal: any = 0, bVal: any = 0;
      switch (this.sortColumn) {
        case 'rank': aVal = a.rank || 0; bVal = b.rank || 0; break;
        case 'company': aVal = a.entity_name || ''; bVal = b.entity_name || ''; break;
        case 'country': aVal = a.country || ''; bVal = b.country || ''; break;
        case 'size': aVal = a.in_theme_invention_count_120m || 0; bVal = b.in_theme_invention_count_120m || 0; break;
        case 'growth': aVal = a.relative_growth_12m || 0; bVal = b.relative_growth_12m || 0; break;
        case 'quality': aVal = a.quality_average_12m || 0; bVal = b.quality_average_12m || 0; break;
        case 'exposure': aVal = a.exposure || 0; bVal = b.exposure || 0; break;
        case 'total_size_10y': aVal = a.all_invention_count_120m || 0; bVal = b.all_invention_count_120m || 0; break;
        case 'innovation_score': aVal = a.innovation_score || 0; bVal = b.innovation_score || 0; break;
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return this.sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  getSortIcon(column: string): string {
    if (this.selectedCategory !== 'all_competitors') return 'bi-arrow-down-up';
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  getCurrentCategoryDescription(): string {
    return this.categoryTabs.find(c => c.key === this.selectedCategory)?.description ?? '';
  }

  getCurrentCategoryTooltip(): string {
    return this.categoryTabs.find(c => c.key === this.selectedCategory)?.tooltip ?? '';
  }

  hasCategoryResults(): boolean {
    return this.filteredCompanyList.length > 0;
  }

  getCurrentCategoryLabel(): string {
    return this.categoryTabs.find(c => c.key === this.selectedCategory)?.label ?? '';
  }

  // --- UI Helpers ---

  toggleQueryBox(): void { this.showQueryBox = !this.showQueryBox; }

  onSearchInputChange(event: any): void {
    const value = event.target.value;
    if (!value || value.trim() === '') {
      this.searchQuery = '';
      this.isSearching = false;
      this.applyFilters();
      return;
    }
    this.isSearching = true;
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.isSearching = false;
    this.applyFilters();
  }

  filterFavorites(): void {
    this.showOnlyFavorites = !this.showOnlyFavorites;
    this.applyFilters();
  }

  private resetState(): void {
    this.allCompanies = [];
    this.modeFilteredCompanies = [];
    this.filteredCompanyList = [];
    this.selectedCategory = 'all_competitors';
    this.paginator_1_page = 1;
    this.searchQuery = '';
    this.isSearching = false;
    this.showOnlyFavorites = false;
    this.countriesControl.setValue([]);
    this.loadingFailed = false;
    this.showDataMissing = false;
    this.showSpinner = false;
  }

  // --- Country Filter ---

  toggleExpand(region: string): void {
    this.expandedRegions.has(region) ? this.expandedRegions.delete(region) : this.expandedRegions.add(region);
  }

  isExpanded(region: string): boolean {
    return this.expandedRegions.has(region);
  }

  toggleRegionSelection(region: any): void {
    const codes = region.countries.map((c: any) => c.code);
    if (this.isRegionFullySelected(region)) {
      this.countriesControl.setValue((this.countriesControl.value ?? []).filter((v: string) => !codes.includes(v)));
    } else {
      this.countriesControl.setValue(Array.from(new Set([...(this.countriesControl.value ?? []), ...codes])));
    }
    this.applyFilters();
  }

  toggleCountrySelection(code: string): void {
    const current = this.countriesControl.value ?? [];
    this.countriesControl.setValue(
      current.includes(code) ? current.filter((v: string) => v !== code) : [...current, code]
    );
    this.applyFilters();
  }

  isRegionFullySelected(region: any): boolean {
    const codes = region.countries.map((c: any) => c.code);
    const selected = this.countriesControl.value ?? [];
    return codes.every((c: string) => selected.includes(c));
  }

  // --- Universe Selector (custom mode) ---

  openUniverseSelector(): void {
    const dialogRef = this.dialog.open(UniverseSelectorComponent, {
      data: {
        min_mcap: this.universeSelection.min_mcap,
        max_mcap: this.universeSelection.max_mcap,
        include_public: this.universeSelection.include_public,
        include_private: this.universeSelection.include_private,
        search_type: 'thematic',
        showCloseButton: true,
        company_type: 'Public Company',
      },
      disableClose: true,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result) {
        this.navigateBasedOnSelection();
        return;
      }

      const newSelection: IUniverseSelection = {
        include_public: result.isPublic,
        include_private: result.isPrivate,
        min_mcap: result.min,
        max_mcap: result.max,
      };

      const onlyPublic = result.isPublic && !result.isPrivate && !result.min && !result.max;
      const onlyPrivate = result.isPrivate && !result.isPublic && !result.min && !result.max;

      if (onlyPublic || onlyPrivate) {
        this.showRedirectDialog(onlyPublic ? 'public' : 'private', newSelection);
        return;
      }

      // Custom peer group — persist and load with backend filtering.
      this.store.dispatch(setUniverseSelection({ selection: newSelection, tabId: this.tabId }));
      this.universeSelection = newSelection;
      this.hasCustomSelection = true;
      this.selectedCategory = 'all_competitors';
      this.loadData();
    });
  }

  private showRedirectDialog(type: 'public' | 'private', selection: IUniverseSelection): void {
    const label = type === 'public' ? 'Public' : 'Private';
    const sideTab = type === 'public' ? 'thematic_public_competitors' : 'thematic_private_competitors';

    const ref = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      disableClose: true,
      data: {
        title: 'Peer Group Selection',
        message: `Looks like you have selected ${label.toLowerCase()} competitors. Redirecting to ${label} Competitors tab.`,
        confirmText: 'OK',
        cancelText: 'Cancel',
        confirmColor: 'primary',
      },
    });

    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      if (confirmed) {
        this.store.dispatch(setUniverseSelection({ selection, tabId: this.tabId }));
        this.store.dispatch(changeActiveSideTab({ sideTab }));
      } else {
        setTimeout(() => this.openUniverseSelector(), 100);
      }
    });
  }

  private navigateBasedOnSelection(): void {
    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(data => {
      const u = data?.universe;
      if (!u) {
        this.store.dispatch(changeActiveSideTab({ sideTab: 'thematic_public_competitors' }));
        return;
      }
      const onlyPub = u.include_public && !u.include_private && !u.min_mcap && !u.max_mcap;
      const onlyPri = u.include_private && !u.include_public && !u.min_mcap && !u.max_mcap;
      if (onlyPub) this.store.dispatch(changeActiveSideTab({ sideTab: 'thematic_public_competitors' }));
      else if (onlyPri) this.store.dispatch(changeActiveSideTab({ sideTab: 'thematic_private_competitors' }));
      else this.store.dispatch(changeActiveSideTab({ sideTab: 'thematic_custom_peer_group' }));
    });
  }

  // --- Actions ---

  newSearch(company: ITopCompanies): void {
    this.store.dispatch(submitSearch({ searchParams: { companies: [company] }, searchType: 'company' }));
  }

  openCompanyPopup(company: ITopCompanies): void {
    const themeParams = this.themeParamsForBackend();
    this.dialog.open(CompanyThemePopupComponent, {
      width: '90vw',
      height: '85vh',
      maxWidth: '1200px',
      maxHeight: '800px',
      position: { top: '80px' },
      data: {
        entityid: company.entityid,
        entity_name: company.entity_name ?? '',
        theme_label: this.tab_name ?? themeParams.cluster_name ?? '',
        theme_search_type: (themeParams.theme_search_type as 'cluster' | 'custom') ?? 'cluster',
        cluster_id: themeParams.cluster_id,
        cluster_name: themeParams.cluster_name,
        query: this.query,
        tabId: this.tabId,
      },
    });
  }

  isPublicCompany(company: ITopCompanies): boolean {
    return company.companytype === 'Public Company' || company.companytype === 'Public Investment Firm';
  }

  hasGICS(company: ITopCompanies): boolean {
    const comp = company as any;
    return !!(comp.gsubind || comp.gsubind_extended || comp.entity_gics || comp.subindustryname);
  }

  isEligibleForWatchlist(company: ITopCompanies): boolean {
    return this.isPublicCompany(company) && this.hasGICS(company);
  }

  toggleCompanySelection(company: ITopCompanies): void {
    this.selectedCompanies.has(company.entityid)
      ? this.selectedCompanies.delete(company.entityid)
      : this.selectedCompanies.add(company.entityid);
  }

  isCompanySelected(entityid: string): boolean {
    return this.selectedCompanies.has(entityid);
  }

  getSelectedEligibleCompanies(): ITopCompanies[] {
    return this.filteredCompanyList.filter(c =>
      this.selectedCompanies.has(c.entityid) && this.isEligibleForWatchlist(c)
    );
  }

  openAddToWatchlist(): void {
    const selected = this.filteredCompanyList.filter(c => this.selectedCompanies.has(c.entityid));
    const eligible = selected.filter(c => this.isEligibleForWatchlist(c));
    const privateCompanies = selected.filter(c => !this.isEligibleForWatchlist(c));

    const mapForWatchlist = (list: ITopCompanies[]) => list.map(c => ({
      entityid: c.entityid,
      entity_name: c.entity_name,
      entity_gics: (c as any).gsubind_extended || ((c as any).gsubind ? (Array.isArray((c as any).gsubind) ? (c as any).gsubind[0] : (c as any).gsubind) : undefined),
      isin: c.isin,
      companytype: c.companytype,
      gsubind: (c as any).gsubind,
      gsubind_extended: (c as any).gsubind_extended,
    }));

    if (privateCompanies.length > 0) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '450px',
        data: {
          title: 'Add to Watchlist',
          message: `Out of ${selected.length} selected companies, ${eligible.length} public companies with valid GICS will be added to the watchlist. ${privateCompanies.length} private companies cannot be added.`,
          confirmText: 'Continue',
          cancelText: 'Cancel',
          confirmColor: 'primary',
        },
      });
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
        if (confirmed && eligible.length > 0) {
          this.dialog.open(AddToWatchlistPopupComponent, {
            width: '600px',
            data: { companies: mapForWatchlist(eligible), tabId: this.tabId },
          });
        }
      });
    } else if (eligible.length > 0) {
      this.dialog.open(AddToWatchlistPopupComponent, {
        width: '600px',
        data: { companies: mapForWatchlist(eligible), tabId: this.tabId },
      });
    }
  }

  downloadExcel(): void {
    const rows = this.modeFilteredCompanies.map(item => ({
      rank: item.rank,
      company: item.entity_name || 'Unknown Company',
      isin: item.isin,
      country: item.country,
      in_theme_size_10y: item.in_theme_invention_count_120m ? Math.round(item.in_theme_invention_count_120m) : item.in_theme_invention_count_120m,
      absolute_growth_12m: item.invention_count_12m ? Math.round(item.invention_count_12m) : item.invention_count_12m,
      relative_growth_12m: item.relative_growth_12m ? Math.round(item.relative_growth_12m * 10) / 10 : item.relative_growth_12m,
      exposure: item.exposure ? Math.round(item.exposure * 10) / 10 : item.exposure,
      total_size_10y: item.all_invention_count_120m ? Math.round(item.all_invention_count_120m) : item.all_invention_count_120m,
      quality_score: item.quality_average_12m ? Math.round(item.quality_average_12m * 10) / 10 : item.quality_average_12m,
      innovation_score: item.innovation_score ? Math.round(item.innovation_score * 10) / 10 : item.innovation_score,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    ws['!cols'] = [
      { wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'top_companies');
    const tabName = this.tab_name ? this.tab_name.substring(0, 30) : 'top_companies';
    XLSX.writeFile(wb, `${tabName}_${this.mode}_competitors.xlsx`);
  }
}
